import {
  ApolloClient,
  InMemoryCache,
  ApolloLink,
  createHttpLink,
} from "@apollo/client";
import { RetryLink } from "@apollo/client/link/retry";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import { Observable } from "@apollo/client/";
import ApolloLinkTimeout from "apollo-link-timeout";
import QueueLink from "apollo-link-queue";
import SerializingLink from "apollo-link-serialize";
import { localGetData } from "./utils/utils";
import * as Sentry from "@sentry/react";
import { sessionRequestHeaders } from "./utils/requestHeaders";
import { v4 as uuidv4 } from "uuid";
import { shouldReportApolloErrorToSentry } from "./utils/apolloErrorUtils";
import { logOutSession } from "./utils/logout";

const testPreferences = localGetData("testPreferences");

/** =========================
 *  Connectivity gating & probe helpers
 *  ========================= */
const env = import.meta.env;
const API_HOST = env.VITE_API_HOST || "";

// Fallback heartbeat endpoints (no server change required)
const FALLBACK_HB_URLS = [
  "https://clients3.google.com/generate_204",
  "https://cloudflare.com/cdn-cgi/trace",
];

// Env-configured heartbeat list (comma-separated)
const ENV_HB_URLS = (env.VITE_HEARTBEAT_URLS || "")
  .split(",")
  .map(s => s.trim())
  .filter(Boolean);

// Use env if provided, otherwise safe fallbacks
const HEARTBEAT_CANDIDATES = ENV_HB_URLS.length
  ? ENV_HB_URLS
  : FALLBACK_HB_URLS;

export function escapeRegex(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}
export function isHttpHttps(urlStr) {
  try {
    const u = new URL(String(urlStr), window.location.href);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}
export function isLocalApiHost(urlStr) {
  try {
    const u = new URL(String(urlStr), window.location.href);
    return ["localhost", "127.0.0.1", "::1"].includes(u.hostname);
  } catch {
    return false;
  }
}
export function isTestingApiHost(urlStr) {
  try {
    const u = new URL(String(urlStr));
    return (
      u.protocol === "https:" &&
      u.hostname === "testing.valcourcloud-vt.com" &&
      u.pathname === "/graphql"
    );
  } catch {
    return false;
  }
}
export function buildAllowlistPatterns(apiHost) {
  const patterns = [];

  // Optional extra allow-regex fragments (comma-separated)
  const envRegex = (env.VITE_CONNECTIVITY_ALLOW_REGEX || "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean);
  envRegex.forEach(rx => {
    try {
      patterns.push(new RegExp(rx, "i"));
    } catch (_) {
      // Ignore invalid optional URL patterns.
    }
  });

  // Literal match for your configured API host (for error flips / tap flips)
  if (apiHost) {
    patterns.push(new RegExp(escapeRegex(apiHost), "i"));
  }

  // Allow real GraphQL operations to flip connectivity (success/err)
  patterns.push(/\/graphql(?:\/|\?|$)/i);

  // Allow heartbeat URLs to flip (use env or fallbacks)
  const hbList = HEARTBEAT_CANDIDATES;
  hbList.forEach(uStr => {
    try {
      const u = new URL(uStr, window.location.href);
      patterns.push(new RegExp(escapeRegex(u.origin + u.pathname), "i"));
    } catch {
      try {
        patterns.push(new RegExp(escapeRegex(uStr), "i"));
      } catch {
        // Ignore values that cannot form a URL pattern.
      }
    }
  });

  // Optional generic health endpoints if you ever add them
  patterns.push(/\/(heartbeat|health|healthz)(?:\/|\?|$)/i);

  return patterns;
}
const ALLOWLIST = buildAllowlistPatterns(API_HOST);

export function canFlipConnectivity(allowlistPatterns, urlOrKey) {
  if (!urlOrKey) return false;
  const s = String(urlOrKey);

  // Generic signals should not directly flip state
  if (s === "browser" || s === "bootstrap") return false;

  // Only honor http/https URLs; ignore data:, blob:, extension, etc.
  if (!isHttpHttps(s)) {
    return allowlistPatterns.some(rx => rx.test(s));
  }
  try {
    const href = new URL(s, window.location.href).href;
    return allowlistPatterns.some(rx => rx.test(href));
  } catch {
    return false;
  }
}

export function shouldBustCache(u) {
  try {
    const path = (u.pathname || "").replace(/\/+$/, "");
    if (/\/graphql$/i.test(path)) return false; // never probe GraphQL anyway
    return true;
  } catch {
    return true;
  }
}

export function isSessionTimeoutError(err) {
  const code = String(err?.extensions?.code || "").toUpperCase();
  const message = String(err?.message || err || "")
    .toLowerCase()
    .replace(/\s+/g, " ")
    .trim();
  return (
    code === "TIMEOUT" ||
    code === "SESSION_TIMEOUT_INVALID" ||
    code === "AUTHENTICATION_REQUIRED" ||
    message === "authentication_required" ||
    message === "your session has expired" ||
    message === "session has expired"
  );
}

export function isSessionTimeoutResult(result) {
  const errors = Array.isArray(result?.errors) ? result.errors : [];
  // Legacy responses put session errors alongside the query data. Do not scan
  // nested mutation errors (for example, an expired password or license).
  const legacyErrors = Array.isArray(result?.data?.errors)
    ? result.data.errors
    : [];

  return (
    errors.some(isSessionTimeoutError) ||
    legacyErrors.some(isSessionTimeoutError)
  );
}

function handleSessionTimeout(operation, reason = "CLIENT_SESSION_EXPIRED") {
  const authorization = operation?.getContext?.()?.headers?.authorization;
  // Only the bearer token sent with this request can authorize its logout.
  // Missing/empty headers must never inherit the current login or match null.
  const token =
    typeof authorization === "string"
      ? authorization.match(/^Bearer\s+(\S+)\s*$/i)?.[1]
      : null;
  if (!token) return;

  console.error(
    `${operation?.operationName}: Your session has expired. Please log in again.`,
  );
  if (logOutSession(reason, token)) {
    window.location.href = "/mp";
  }
}

export function createSessionTimeoutLink(handleTimeout = handleSessionTimeout) {
  return new ApolloLink((operation, forward) => {
    return new Observable(observer => {
      let closed = false;
      let timedOut = false;
      let subscription = null;
      let unsubscribeRequested = false;

      const requestUnsubscribe = () => {
        if (subscription) {
          subscription.unsubscribe();
        } else {
          unsubscribeRequested = true;
        }
      };

      const safeComplete = () => {
        if (closed) {
          return;
        }
        closed = true;
        observer.complete();
      };

      const safeError = err => {
        if (closed) {
          return;
        }
        closed = true;
        observer.error(err);
      };

      const sub = forward(operation).subscribe({
        next: result => {
          if (closed || timedOut) {
            return;
          }
          if (isSessionTimeoutResult(result)) {
            timedOut = true;
            requestUnsubscribe();
            try {
              handleTimeout(operation);
            } catch (err) {
              safeError(err);
              return;
            }
            closed = true;
            return;
          }
          observer.next(result);
        },
        error: err => {
          if (closed || timedOut) {
            return;
          }
          safeError(err);
        },
        complete: () => {
          if (closed || timedOut) {
            return;
          }
          safeComplete();
        },
      });

      subscription = sub;
      if (unsubscribeRequested) {
        subscription.unsubscribe();
      }

      return () => subscription?.unsubscribe();
    });
  });
}

/** Probe a list of candidate URLs; resolve with the first URL that fetches. */
export async function probeConnectivityOnce(candidates) {
  for (let i = 0; i < candidates.length; i++) {
    const raw = candidates[i];
    if (!raw) continue;
    // Only probe urls that *could* flip connectivity per gating
    if (!canFlipConnectivity(ALLOWLIST, raw)) continue;

    try {
      const u = new URL(raw, window.location.href);
      const href = shouldBustCache(u)
        ? u.href + (u.search ? "&ts=" + Date.now() : "?ts=" + Date.now())
        : u.href;

      // no-cors is fine; an opaque 200/204/405 still proves network reachability
      await fetch(href, {
        method: "GET",
        mode: "no-cors",
        cache: shouldBustCache(u) ? "no-store" : "reload",
        redirect: "follow",
      });
      return u.href;
    } catch (_) {
      // try next
    }
  }
  return null;
}

/** Triggered by browser/app "online" events: verify with a gated probe, then flip. */
export function triggerConnectivityRecovery(handleOnlineState) {
  // 🔑 Only heartbeat URLs here (NO GraphQL/API host)
  const candidates = HEARTBEAT_CANDIDATES.slice();
  probeConnectivityOnce(candidates).then(okUrl => {
    if (okUrl) {
      handleOnlineState(true, okUrl); // allowed by ALLOWLIST
    }
  });
}
/** ========================= */

const getApolloClient = async (setOnlineState, setTimestamp, setErrorObj) => {
  const api_host = API_HOST;
  const enableApolloDevTools =
    (env.DEV && isLocalApiHost(api_host)) || isTestingApiHost(api_host);

  const httpLink = createHttpLink({
    uri: api_host,
    mode: "no-cors",
  });

  const offlineLink = new QueueLink();

  const authLink = setContext((_, { headers }) => {
    const token = localGetData("mp_token");
    return {
      headers: {
        ...headers,
        ...sessionRequestHeaders(token),
      },
    };
  });

  const idempotencyLink = setContext((operation, { headers }) => {
    const isMutation = operation.query.definitions.some(definition => {
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "mutation" &&
        (!operation.operationName ||
          definition.name?.value === operation.operationName)
      );
    });

    if (!isMutation) return {};

    return {
      headers: {
        ...headers,
        "x-idempotency-id": headers?.["x-idempotency-id"] || uuidv4(),
      },
    };
  });

  const ignoredOperations = ["GET_BULLETINS_QUERY", "GET_ORIS_QUERY"];
  const timeLink = setContext((operation, { headers }) => {
    const timestamp = Math.floor(Date.now() / 1000);
    const { variables } = operation;
    let noSessionReset = false;
    const shouldIgnore = ignoredOperations.includes(operation.operationName);

    if (Object.prototype.hasOwnProperty.call(variables, "input")) {
      noSessionReset = variables?.input?.noSessionReset;
    } else {
      noSessionReset = variables?.noSessionReset;
    }
    if (!noSessionReset && setTimestamp && !shouldIgnore) {
      setTimestamp(timestamp);
    }
    return {
      headers: {
        ...headers,
        timestamp: timestamp,
      },
    };
  });

  // ---- GATED central toggle ----
  let reconnectsNeeded = localGetData("reconnectsNeeded") || 2;
  let reconnectNum = 0;

  const handleOnlineState = (bool, sourceUrlOrKey) => {
    if (!canFlipConnectivity(ALLOWLIST, sourceUrlOrKey)) {
      return; // ignore non-allowlisted sources
    }

    if (bool) {
      if (reconnectNum >= reconnectsNeeded) {
        offlineLink.open();
        !!setOnlineState && setOnlineState(true);
        reconnectNum = 0;
      } else {
        reconnectNum++;
        return;
      }
    } else {
      reconnectNum = 0;
      offlineLink.close();
      !!setOnlineState && setOnlineState(false);
    }
  };
  // ---- end gating ----

  const errorLink = onError(
    ({ graphQLErrors, networkError, response, operation }) => {
      if (graphQLErrors) {
        for (const error of graphQLErrors) {
          if (isSessionTimeoutError(error)) {
            handleSessionTimeout(operation);
            if (response?.errors) response.errors = null;
            return;
          } else if (error === "UNAUTHORIZED") {
            alert(`Unauthorized access.`);
            handleSessionTimeout(operation, "CLIENT_UNAUTHORIZED");
            if (response?.errors) response.errors = null;
            return;
          } else if (error === "ABORT") {
            alert(`Operation not completed. Please refresh.`);
            if (response?.errors) response.errors = null;
            return;
          } else {
            console.error(
              `GraphQL Error \n\n${operation?.operationName} threw the following error: \n\n${error.message}`,
            );
            setErrorObj({
              open: true,
              message: `${operation?.operationName}`,
            });
            return;
          }
        }
      }
      if (networkError) {
        if (shouldReportApolloErrorToSentry(networkError)) {
          Sentry.captureException(networkError, {
            extra: {
              operationName: operation.operationName,
              variables: operation.variables,
              query: operation.query.loc?.source.body,
              networkError: networkError.message,
            },
          });
        }
        console.error(
          `Network Error. \n${operation?.operationName} threw the following error: \n${networkError}`,
        );
        // Flip OFFLINE (gated) using the API host (allowed by allowlist)
        const opUrl =
          (operation && operation.getContext && operation.getContext()?.uri) ||
          api_host;
        handleOnlineState(false, opUrl);
        return Observable.of(operation);
      }
    },
  );

  // Any successful GraphQL response reaffirms ONLINE (gated)
  const connectivityTapLink = new ApolloLink((operation, forward) => {
    const opUrl =
      (operation && operation.getContext && operation.getContext()?.uri) ||
      api_host;
    return new Observable(observer => {
      const sub = forward(operation).subscribe({
        next: result => {
          handleOnlineState(true, opUrl);
          observer.next(result);
        },
        error: err => observer.error(err),
        complete: () => observer.complete(),
      });
      return () => sub.unsubscribe();
    });
  });

  const sessionTimeoutLink = createSessionTimeoutLink();

  console.log(`Connected to: ${env.VITE_API_HOST}`);

  // Initial state: if browser says online, *probe heartbeat only*; else go offline.
  if (window.navigator.onLine || env.VITE_FORCE_ONLINE === "true") {
    triggerConnectivityRecovery(handleOnlineState);
  } else {
    handleOnlineState(false, api_host);
  }

  // Browser offline: gated flip with API host as the source
  window.addEventListener("offline", () => {
    if (env.VITE_FORCE_ONLINE === "true") return;
    handleOnlineState(false, api_host);
  });

  // Browser online: treat as a trigger, not truth; probe heartbeat, then flip (gated)
  window.addEventListener("online", () => {
    if (env.VITE_FORCE_ONLINE === "true") return;
    triggerConnectivityRecovery(handleOnlineState);
  });

  // If you’re running the secondary poller, it dispatches "app:online" on success.
  // Use it as another trigger to re-probe heartbeats.
  window.addEventListener("app:online", () => {
    if (env.VITE_FORCE_ONLINE === "true") return;
    triggerConnectivityRecovery(handleOnlineState);
  });

  const cache = new InMemoryCache({});

  const retryLink = new RetryLink({
    delay: {
      initial: 500,
      max: Infinity,
      jitter: true,
    },
    attempts: {
      max: 5,
      retryIf: (error) => !!error,
    },
  });

  const serializeLink = new SerializingLink();

  const timeoutLink = new ApolloLinkTimeout(
    testPreferences?.graphQlTimeout || 600000,
  );

  return new ApolloClient(
    {
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "network-only",
          errorPolicy: "ignore",
        },
        query: {
          fetchPolicy: "network-only",
          errorPolicy: "all",
        },
        mutate: {
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        },
      },
      link: ApolloLink.from([
        timeLink,
        idempotencyLink,
        errorLink,
        retryLink,
        // The queue stays closed while "offline"; open on gated success
        offlineLink,
        serializeLink,
        // Any successful GraphQL response reaffirms ONLINE (gated)
        connectivityTapLink,
        sessionTimeoutLink,
        timeoutLink,
        authLink.concat(httpLink),
      ]),
      connectToDevTools: enableApolloDevTools,
      cache,
    },
    handleOnlineState,
  );
};

export default getApolloClient;
